/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    IPayLoad,
    isMobile,
    ITelemetryContent,
    TelemetryConstant,
    VariantType
} from '@msdyn365-commerce-modules/utilities';

import {
    getUpdatedRefinementCriteria,
    IRefinerApplyProps,
    IRefinerChoiceSummaryClickNotification,
    IRefinerChoiceSummaryProps,
    IRefinerFooterProps,
    IRefinerHeaderProps,
    IRefinerItemData,
    IRefinerItemToggleNotification,
    IRefinerModalProps,
    IRefinerModalToggleProps,
    IRefinerProps,
    IRefinerSortOption,
    IRefinerSortProps,
    IRefinerToggleProps,
    ISearchBarOption,
    ISearchBarProps,
    isMatchingRefinementCriterion,
    RefinerOptionsComponent,
    RefinerOptionsType
} from '@msdyn365-commerce/components';

import classname from 'classnames';
import { computed, observable, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    OrderOriginator,
    OrderSearchCriteria,
    SalesOrder,
    SalesStatus,
    BusinessPartner,
    BusinessPartnersDataActions,
    BusinessPartnerUsersDataActions,
    BusinessPartnerUser
} from '@msdyn365-commerce/retail-proxy';

interface IB2bUser {
    isB2bContact?: boolean;
    isB2bAdmin?: boolean;
}
import { Label } from '../../common/label';
import {
    getGetOrderHistoryWithSearchAndFiltersAction,
    GetOrderHistoryWithSearchAndFiltersInput,
    IGetOrderHistoryWithSearchAndFiltersSorting,
    IOrderHistoryWithSearchAndFiltersResult
} from './actions/get-order-history-with-search-and-filters';
import { getOriginatorsAction, GetOrderOriginatorsInput } from '../../actions/get-sales-order-originators';
import {
    IOrderHistoryWithSearchAndFiltersHeaderProps,
    IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps,
    OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState,
    OrderHistoryWithSearchAndFiltersViewModeState,
    IOrderHistoryWithSearchAndFiltersViewModeProps,
    OrderHistoryWithSearchAndFiltersViewModeComponent,
    OrderHistoryWithSearchAndFiltersOrderTableComponent
} from './components';
import { IOrderHistoryWithSearchAndFiltersData } from './order-history-with-search-and-filters.data';
import { IOrderHistoryWithSearchAndFiltersProps } from './order-history-with-search-and-filters.props.autogenerated';
import { SearchOption } from './common/search-option';
import { mapISearchBarOptionIntoSearchOption, mapSearchOptionIntoISearchBarOption } from './common/search-option-mappings';
import { DateRefinerOptionItem, RefinerSourceType } from './common/refiner-option';
import {
    getDateFromString,
    getDateRange,
    mapChannelIdentityIntoIRefinerItemData,
    mapDateRefinerOptionIntoIRefinerItemData,
    mapIRefinerItemDataIntoDateRefinerOptionItem,
    mapIRefinerItemDataIntoOrderStatusRefinerOptionItem,
    mapOrderStatusRefinerOptionIntoIRefinerItemData,
    mapOrderStatusRefinerOptionItemIntoSalesStatus,
    mapOrganizationIntoIRefinerItemData,
    mapContactsIntoDeduplicatedRefinerItems
} from './common/refiner-option-mappings';
import { OrderHistoryWithSearchAndFiltersOrderListComponent } from './components/order-history-with-search-and-filters-order-list.component';
import { mapIRefinerSortOptionIntoSortOption, mapSortOptionIntoIRefinerSortOption } from './common/sort-option-mappings';
import { SortOption } from './common/sort-option';

interface IOrderHistoryWithSearchAndFiltersViewRefinerState {
    isModalOpen: boolean;
    isExpanded: boolean;
    currentSortOption?: IRefinerSortOption;
}

interface IOrderHistoryWithSearchAndFiltersViewSearchBarState {
    currentSearchOption?: ISearchBarOption;
    currentSearchText?: string;
}

export interface IOrderHistoryWithSearchAndFiltersViewState {
    isDataReady: boolean;
    hasError: boolean;
    isLoading: boolean;
    isShowMoreButton: boolean;
    errorCode?: string;
    errorMessage?: string;
}

export interface IOrderHistoryWithSearchAndFiltersViewProps
    extends IOrderHistoryWithSearchAndFiltersProps<IOrderHistoryWithSearchAndFiltersData> {
    orderHistoryWithSearchAndFiltersProps: IModuleProps;
    headerProps: IOrderHistoryWithSearchAndFiltersHeaderProps;
    organizationWideSelectorProps?: IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps;
    viewState: IOrderHistoryWithSearchAndFiltersViewState;
    searchBarProps?: ISearchBarProps;
    refinerProps: IRefinerProps;
    orderHistorywithSearchAndFiltersResult: IOrderHistoryWithSearchAndFiltersResult;
    alert?: React.ReactNode;
    loading?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    backToShoppingLink?: React.ReactNode;
    list?: React.ReactNode;
    table?: React.ReactNode;
    moreButton?: React.ReactNode;
}

export interface IOrderHistoryWithSearchAndFiltersViewContactFilterState {
    organizationList: BusinessPartner[];
    contact: BusinessPartnerUser[];
    loaded: boolean;
    businessPartnerErrorMessage: string;
}

/**
 * Grid size enum.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * OrderHistoryWithSearchAndFilters component.
 * @extends {React.Component<IOrderHistoryWithSearchAndFiltersProps<IOrderHistoryWithSearchAndFiltersData>>}
 */
@observer
class OrderHistoryWithSearchAndFilters extends React.Component<
    IOrderHistoryWithSearchAndFiltersProps<IOrderHistoryWithSearchAndFiltersData>
> {
    @observable private orderHistoryWithSearchAndFilters!: IOrderHistoryWithSearchAndFiltersResult;

    @observable private viewState!: IOrderHistoryWithSearchAndFiltersViewState;

    @observable private _refinerState!: IOrderHistoryWithSearchAndFiltersViewRefinerState;

    @observable private _contactFilterState!: IOrderHistoryWithSearchAndFiltersViewContactFilterState;

    @observable private _activeRefinementCriteria: IRefinerItemData[];

    private originatorList!: OrderOriginator[];

    @observable private _ordersViewModeState: OrderHistoryWithSearchAndFiltersViewModeState;

    @observable private _viewport: GridSize;

    private _organizationWideSelectorState: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState;

    private pageSize!: number;

    private readonly telemetryContent?: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private readonly _refinerModalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _isB2bContact: boolean;

    private readonly _isB2bAdmin: boolean;

    private _searchBarState: IOrderHistoryWithSearchAndFiltersViewSearchBarState;

    private readonly orderHistoryClassName = 'ms-order-history-with-search-and-filters';

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    constructor(props: IOrderHistoryWithSearchAndFiltersProps<IOrderHistoryWithSearchAndFiltersData>) {
        super(props);
        this.initState();
        this._organizationWideSelectorState = OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.CurrentUser;
        this._ordersViewModeState = OrderHistoryWithSearchAndFiltersViewModeState.DetailedView;
        this._searchBarState = {
            currentSearchOption: undefined,
            currentSearchText: ''
        };
        this._activeRefinementCriteria = [];
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this._refinerModalToggleRef = React.createRef<HTMLButtonElement>();
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._updateViewport = this._updateViewport.bind(this);
        const user = (this.props.context.request.user ?? {}) as IB2bUser;
        this._isB2bContact = user.isB2bContact === true;
        this._isB2bAdmin = user.isB2bAdmin === true;
        this._contactFilterState = {
            organizationList: [],
            contact: [],
            loaded: false,
            businessPartnerErrorMessage: ''
        };
    }

    public componentDidMount(): void {
        if (this.props.context.actionContext.requestContext.user.isAuthenticated) {
            this._updateOrderHistory();
        } else {
            this.viewState.isDataReady = true;
        }

        void this._loadContactFilterData();

        if (Msdyn365.msdyn365Commerce.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }

        this._activeRefinementCriteria = [];
    }

    public componentWillUnmount(): void {
        if (Msdyn365.msdyn365Commerce.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public shouldComponentUpdate(
        nextProps: IOrderHistoryWithSearchAndFiltersProps<IOrderHistoryWithSearchAndFiltersData>,
        nextState: IOrderHistoryWithSearchAndFiltersViewState
    ): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className, shouldShowCount },
            resources: { genericErrorMessage, noOrderHistoryLable, moreButtonText, backToShopping, loadingLabel }
        } = this.props;
        const { salesOrders, products } = this.orderHistoryWithSearchAndFilters;
        const { isDataReady, hasError, isShowMoreButton, isLoading } = this.viewState;
        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const channelIdentities = this.props.data.channels.result && this.props.data.channels.result.channelIdentities;
        const orgUnitLocations = this.props.data.orgUnitLocations.result && this.props.data.orgUnitLocations.result.orgUnitLocations;
        const customer = this.props.data.customerInformation && this.props.data.customerInformation.result;
        const b2bUserInformation = this.props.data.b2bUserInformation && this.props.data.b2bUserInformation.result;

        let viewProps: IOrderHistoryWithSearchAndFiltersViewProps = {
            ...this.props,
            orderHistorywithSearchAndFiltersResult: this.orderHistoryWithSearchAndFilters,
            viewState: this.viewState,
            orderHistoryWithSearchAndFiltersProps: {
                moduleProps: this.props,
                className: classname(this.orderHistoryClassName, className)
            },
            headerProps: this._getHeaderProps(),
            searchBarProps: this._getSearchBarProps(),
            refinerProps: this._getRefinerProps(),
            organizationWideSelectorProps: this._getOrganizationWideSelectorProps(),
            backToShoppingLink: backToShoppingUrl && (
                <Button className={`${this.orderHistoryClassName}__btn-keep-shopping`} href={backToShoppingUrl}>
                    {backToShopping}
                </Button>
            )
        };

        if (hasError) {
            viewProps = {
                ...viewProps,
                alert: <Label className={`${this.orderHistoryClassName}__alert`} label={genericErrorMessage} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!isDataReady) {
            viewProps = {
                ...viewProps,
                loading: <Label className={`${this.orderHistoryClassName}__loading`} label={loadingLabel} />
            };
            return this.props.renderView(viewProps) as React.ReactElement;
        }

        if (!salesOrders || salesOrders.length === 0) {
            if (this.viewState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_AuthorizationFailed') {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className={`${this.orderHistoryClassName}__empty-message`}>{this.viewState.errorMessage}</p>
                };
            } else {
                viewProps = {
                    ...viewProps,
                    emptyMessage: <p className={`${this.orderHistoryClassName}__empty-message`}>{noOrderHistoryLable}</p>
                };
            }

            return this.props.renderView(viewProps) as React.ReactElement;
        }

        this.payLoad.contentAction.etext = TelemetryConstant.ViewMore;
        const viewMoreAttributes = getTelemetryAttributes(this.telemetryContent!, this.payLoad);

        const productsDetailedViewLimit = 5;

        const isRetailB2BContactMultiOutletOrderingFeatureEnabled: boolean = this._isB2bContact;

        viewProps = {
            ...viewProps,
            headerProps: {
                ...viewProps.headerProps,
                showOrderCount: shouldShowCount ?? true
            },
            table:
                this._ordersViewModeState === OrderHistoryWithSearchAndFiltersViewModeState.ListView ? (
                    <OrderHistoryWithSearchAndFiltersOrderTableComponent
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        className={`${this.orderHistoryClassName}__table-list`}
                        showChannelInfo={this.props.config.showChannelInfo ?? false}
                        showOrderPlacedBy={Msdyn365.isChannelTypeB2B(this.props.context.request) ?? false}
                        isMobileView={this.isMobile}
                        telemetryContent={this.telemetryContent}
                        data={{
                            salesOrders,
                            channelIdentities,
                            orgUnitLocations,
                            customer,
                            originators: this.originatorList,
                            b2bUserInformation
                        }}
                        resources={this.props.resources}
                        isRetailB2BContactMultiOutletOrderingFeatureEnabled={isRetailB2BContactMultiOutletOrderingFeatureEnabled}
                    />
                ) : (
                    undefined
                ),
            list:
                this._ordersViewModeState === OrderHistoryWithSearchAndFiltersViewModeState.DetailedView ? (
                    <OrderHistoryWithSearchAndFiltersOrderListComponent
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        className={`${this.orderHistoryClassName}__sales-order-list`}
                        showChannelInfo={this.props.config.showChannelInfo ?? false}
                        showOrderPlacedBy={Msdyn365.isChannelTypeB2B(this.props.context.request) ?? false}
                        isMobileView={this.isMobile}
                        salesLinesLimit={productsDetailedViewLimit}
                        telemetryContent={this.telemetryContent}
                        data={{
                            salesOrders,
                            products,
                            channelIdentities,
                            orgUnitLocations,
                            customer,
                            originators: this.originatorList
                        }}
                        resources={this.props.resources}
                        moduleProps={this.props}
                        isRetailB2BContactMultiOutletOrderingFeatureEnabled={isRetailB2BContactMultiOutletOrderingFeatureEnabled}
                    />
                ) : (
                    undefined
                ),
            moreButton: isShowMoreButton && (
                <Button
                    className={`${this.orderHistoryClassName}__btn-more ${isLoading ? 'is-busy' : ''}`}
                    {...viewMoreAttributes}
                    disabled={isLoading}
                    onClick={this._onMoreButtonClick}
                >
                    {moreButtonText}
                </Button>
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _loadContactFilterData = async (): Promise<void> => {
        if (!this._isB2bContact) {
            return;
        }

        await Promise.all([this._loadOrganizations(), this._getUniqueContact()]);
    };

    /**
     * Initializes the view state.
     */
    private readonly initState = () => {
        this.viewState = {
            isDataReady: false,
            hasError: false,
            isShowMoreButton: (this.props.config.pageSize || 0) > 0,
            isLoading: false,
            errorCode: '',
            errorMessage: ''
        };
        this.originatorList = [];
        this.pageSize = this.props.config.pageSize || 10;
        this.orderHistoryWithSearchAndFilters = {
            salesOrders: [],
            products: [],
            paging: {
                top: this.pageSize,
                nextPageToken: '0,0,0'
            }
        };
        this._refinerState = {
            isModalOpen: false,
            isExpanded: false,
            currentSortOption: (() => {
                // keep existing sort option if it exists
                if (this._refinerState?.currentSortOption) {
                    return this._refinerState.currentSortOption;
                }
                // otherwise, use the first sort option if it exists
                const sortOptions = this.props.data?.sortOptions?.result ?? [];
                const sortOption = sortOptions.length > 0 ? sortOptions[0] : undefined;
                return sortOption ? mapSortOptionIntoIRefinerSortOption(sortOption, this.props.resources) : undefined;
            })()
        };
    };

    /**
     * Handles the heading change.
     */
    private _handleOrderHistoryHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.heading!.text = event.target.value);

    /**
     * Handles the viewport change.
     * Updates the viewport state.
     */
    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this._refinerState.isModalOpen && !this.isMobile) {
            this._toggleRefinerModal();
        }
    }

    private _getViewport(): GridSize {
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    /**
     * Handles the click on a choice in refiner choice summary.
     * Removes the choice from refinement criteria.
     * @param notification
     */
    private readonly _clickRefinerChoice = (notification: IRefinerChoiceSummaryClickNotification): void => {
        const { telemetry } = this.props;
        let currentRefinementCriteria = this._activeRefinementCriteria ?? [];

        if (!notification.shouldClearAll) {
            const selectedChoice: IRefinerItemData | undefined = notification.selectedRefinerItem;
            if (!selectedChoice) {
                telemetry.warning(`[${this.orderHistoryClassName}._clickRefinerChoice] Choice could not be determined`);
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: IRefinerItemData) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        this._activeRefinementCriteria = currentRefinementCriteria;

        // if the refiner is collapsed, apply the refinement criteria automatically
        if (!this._refinerState.isExpanded) {
            this._applyRefinerChoices();
        }
    };

    /**
     * Handles the click on refiner clear all option.
     * Removes all choices from refinement criteria.
     * @param notification
     */
    private readonly _clickRefinerClearAll = (notification: IRefinerChoiceSummaryClickNotification): void => {
        this._clickRefinerChoice(notification);
    };

    /**
     * Handles the click on refiner item.
     * Updates the refiner item state and the refinement criteria.
     * @param notification The selected refiner item.
     */
    private readonly _toggleRefinerItem = (notification: IRefinerItemToggleNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required
        if (!requestContext || !actionContext) {
            const error = `Refiner cannot refine search criteria:
            ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''} could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        const updatedRefinementCriteria = getUpdatedRefinementCriteria(notification, this._activeRefinementCriteria || []);
        transaction(() => {
            this._activeRefinementCriteria = updatedRefinementCriteria;
        });
    };

    /**
     * Handles the click on refiner toggle.
     * Updates the refiner modal state.
     */
    private readonly _toggleRefinerModal = (): void => {
        this._refinerState.isModalOpen = !this._refinerState.isModalOpen;
    };

    /**
     * Handles the click on apply button.
     * Resets the view state and updates the order history information.
     */
    private readonly _applyRefinerChoices = (): void => {
        this.initState();
        this._updateOrderHistory();
    };

    /**
     * Handles the click on more button.
     * Updates the order history information.
     */
    private readonly _onMoreButtonClick = (): void => {
        this._updateOrderHistory();
    };

    /**
     * Handles the click on order view mode option.
     * Updates the order view mode information.
     * @param state The new orders view mode state.
     */
    private readonly _clickOrderViewMode = (state: OrderHistoryWithSearchAndFiltersViewModeState) => {
        this._ordersViewModeState = state;
    };

    /**
     * Handles the click on organization wide selector option.
     * It updates the organization wide selector state and updates the order history information.
     * @param state The new organization wide selector state.
     */
    private readonly _clickOrganizationWideSelectorOption = (state: OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState) => {
        this.initState();
        this._organizationWideSelectorState = state;
        this._updateOrderHistory();
    };

    /**
     * Handles the click on search button.
     */
    private readonly _clickSearchButton = (searchOption?: ISearchBarOption, searchText?: string): void => {
        this.initState();
        this._searchBarState = {
            currentSearchOption: searchOption,
            currentSearchText: searchText
        };
        this._updateOrderHistory();
    };

    /**
     * Handles the selection of a sort option.
     */
    private readonly _selectRefinerSortOption = (sortOption: IRefinerSortOption): void => {
        if (this._refinerState.isExpanded || this._refinerState.isModalOpen) {
            this._refinerState.currentSortOption = sortOption;
        } else {
            this.initState();
            this._refinerState.currentSortOption = sortOption;
            this._updateOrderHistory();
        }
    };

    /**
     * Gets the order originator information for each order.
     */
    private readonly getOriginator = async (salesOrders: SalesOrder[]): Promise<void> => {
        if (Msdyn365.isUserTypeB2B(this.props.context.request)) {
            const actionContext = this.props.context.actionContext;
            let result: OrderOriginator[] | undefined;
            try {
                const salesIds = salesOrders.map(order => order.SalesId ?? '').filter(id => id !== '');
                const transactionIds = salesOrders.map(order => order.Id ?? '').filter(id => id !== '');
                const originatorsInput = new GetOrderOriginatorsInput(transactionIds, salesIds, this.props.context.request.apiSettings);
                result = await getOriginatorsAction(originatorsInput, actionContext);
            } catch (error) {
                result = undefined;
            }
            if (result) {
                this.originatorList.push(...result);
            }
        }
    };

    /**
     * Gets the order history information and updates the view state.
     */
    private readonly _updateOrderHistory = () => {
        this.getOrderHistory()
            .then(() => {
                this.viewState.isDataReady = true;
            })
            .catch(() => {
                this.viewState.hasError = true;
            });
    };

    /**
     * Gets the order history information and updates the view state.
     */
    private readonly getOrderHistory = async (): Promise<void> => {
        const actionContext = this.props.context.actionContext;

        let searchCriteria: OrderSearchCriteria | undefined;
        try {
            searchCriteria = this._getSearchCriteria();
        } catch (error) {
            // update order history information
            this.orderHistoryWithSearchAndFilters.products.push(...[]);
            this.orderHistoryWithSearchAndFilters.salesOrders.push(...[]);

            // update pagination information
            this.orderHistoryWithSearchAndFilters.paging = {
                top: this.pageSize,
                nextPageToken: '0,0,0'
            };
            this.viewState.isShowMoreButton = false;
            this.viewState.isLoading = false;
            return Promise.resolve();
        }

        const b2bUser = this.props.data.b2bUserInformation?.result;
        const input = new GetOrderHistoryWithSearchAndFiltersInput(
            this.orderHistoryWithSearchAndFilters.paging,
            this.props.context.request.apiSettings,
            this._organizationWideSelectorState,
            searchCriteria,
            this._getSortingCriteria(),
            b2bUser?.ContactPersonId
        );

        try {
            this.viewState.isLoading = true;
            const response = await getGetOrderHistoryWithSearchAndFiltersAction(input, actionContext);
            if (response) {
                const { products, salesOrders, paging, error } = response;

                if (error && error.statusCode && error.statusCode === 500) {
                    this.viewState.errorMessage = this.props.resources.orderHistoryApiTimeoutErrorMessage;
                    this.viewState.isLoading = false;
                    return Promise.reject();
                }
                // update order history information
                this.orderHistoryWithSearchAndFilters.products.push(...products);
                this.orderHistoryWithSearchAndFilters.salesOrders.push(...salesOrders);

                // update pagination information
                this.orderHistoryWithSearchAndFilters.paging = paging;
                this.viewState.isShowMoreButton =
                    this.viewState.isShowMoreButton && response.salesOrders.length > 0 && response.salesOrders.length === this.pageSize;

                // retrieve originator information
                await this.getOriginator(salesOrders);
            }
            this.viewState.isLoading = false;
            await Promise.resolve();
            return;
        } catch (error) {
            this.viewState.isLoading = false;

            // check the error to determine if the error is due to authorization failure
            if (error?.error?.name === 'Microsoft_Dynamics_Commerce_Runtime_AuthorizationFailed') {
                this.viewState.errorCode = 'Microsoft_Dynamics_Commerce_Runtime_AuthorizationFailed';
                this.viewState.errorMessage = error.error?.name;
                return Promise.reject();
            }

            return Promise.reject();
        }
    };

    /**
     * Gets the order search criteria based on the active refinement criteria.
     * @returns The order search criteria.
     * @throws Error if the channel name is not found.
     */
    private readonly _getSearchCriteria = (): OrderSearchCriteria => {
        const orderSearchCriteria: OrderSearchCriteria = {};

        // retrieve the channel refiner information and populate the search criteria
        const channelIds: number[] = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === RefinerSourceType.Origin && !isNaN(Number(refinement.value)))
            .map(refinement => Number(refinement.value));
        if (channelIds.length > 0) {
            orderSearchCriteria.ChannelIds = channelIds;
        }

        // retrieve the order status refiner information and populate the search criteria
        const statusValues: SalesStatus[] = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === RefinerSourceType.OrderStatus)
            .map(refinement => mapIRefinerItemDataIntoOrderStatusRefinerOptionItem(refinement))
            .map(orderStatusRefinerItem => mapOrderStatusRefinerOptionItemIntoSalesStatus(orderStatusRefinerItem))
            .filter(salesStatus => salesStatus !== undefined) as SalesStatus[];
        if (statusValues.length > 0) {
            orderSearchCriteria.OrderStatusValues = statusValues;
        }

        // retrieve the date refiner information and populate the search criteria
        const dates: IRefinerItemData[] = this._activeRefinementCriteria.filter(
            refinement => refinement.sourceTypeValue === RefinerSourceType.CreatedDate
        );
        if (dates.length === 1) {
            const dateOption: DateRefinerOptionItem | undefined = mapIRefinerItemDataIntoDateRefinerOptionItem(dates[0]);
            if (dateOption === DateRefinerOptionItem.Customized) {
                orderSearchCriteria.StartDateTime = getDateFromString(dates[0].minValue);
                orderSearchCriteria.EndDateTime = getDateFromString(dates[0].maxValue);
            } else {
                const { startDate, endDate } = getDateRange(dates[0].refinerItemId);
                orderSearchCriteria.StartDateTime = startDate;
                orderSearchCriteria.EndDateTime = endDate;
            }
        } else if (dates.length > 1) {
            this.props.telemetry.warning(`[${this.orderHistoryClassName}._getSearchCriteria] Multiple date refiners are selected`);
        }

        // retrieve the organization refiner information and populate the search criteria
        const customerAccountNumbers: string[] = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === RefinerSourceType.Organization && refinement.value)
            .map(refinement => refinement.value as string);
        if (customerAccountNumbers.length > 0) {
            orderSearchCriteria.CustomerAccountNumbers = customerAccountNumbers;
        }

        // retrieve the contact refiner information and populate the search criteria
        const contactPersonIds: string[] = this._activeRefinementCriteria
            .filter(refinement => refinement.sourceTypeValue === RefinerSourceType.Contact && refinement.value)
            .flatMap(refinement => (refinement.value as string).split(','))
            .filter(id => id.length > 0);
        if (contactPersonIds.length > 0) {
            orderSearchCriteria.ContactPersonIds = contactPersonIds;
        }

        // retrieve the search option information and populate the search criteria
        const searchOption: SearchOption | undefined = mapISearchBarOptionIntoSearchOption(this._searchBarState.currentSearchOption);

        if (searchOption === SearchOption.OrderNumber) {
            orderSearchCriteria.SalesId = this._searchBarState.currentSearchText;
        }

        if (searchOption === SearchOption.ConfirmationId) {
            orderSearchCriteria.ChannelReferenceId = this._searchBarState.currentSearchText;
        }

        if (searchOption === SearchOption.CustomerRequisition) {
            orderSearchCriteria.CustomerRequisition = this._searchBarState.currentSearchText;
        }

        if (searchOption === SearchOption.ChannelName) {
            const channelIdentities = (this.props.data.channels.result && this.props.data.channels.result.channelIdentities) ?? [];
            const searchChannelName = this._searchBarState.currentSearchText ?? '';

            if (searchChannelName) {
                orderSearchCriteria.ChannelIds = channelIdentities
                    .filter(channel => channel.Name?.toLowerCase()?.includes(searchChannelName.toLocaleLowerCase().replace('*', '')))
                    .map(channel => channel.RecordId);

                // we must not return orders if there is no channel whose name is equals to given search text.
                if (orderSearchCriteria.ChannelIds.length === 0) {
                    throw new Error('No channel found with given channel name.');
                }
            }
        }

        return orderSearchCriteria;
    };

    /**
     * Gets the sorting criteria based on the refiner sort option.
     * @returns The sorting criteria.
     */
    private readonly _getSortingCriteria = (): IGetOrderHistoryWithSearchAndFiltersSorting | undefined => {
        let sorting: IGetOrderHistoryWithSearchAndFiltersSorting | undefined;
        const sortOption: SortOption | undefined = mapIRefinerSortOptionIntoSortOption(this._refinerState.currentSortOption);
        switch (sortOption) {
            case SortOption.CreatedDateTimeAscending:
                sorting = {
                    columnName: 'CREATEDDATETIME',
                    isDescending: false
                };
                break;
            case SortOption.CreatedDateTimeDescending:
                sorting = {
                    columnName: 'CREATEDDATETIME',
                    isDescending: true
                };
                break;
            case SortOption.ConfirmedShipDateAscending:
                sorting = {
                    columnName: 'CONFIRMEDSHIPDATE',
                    isDescending: false
                };
                break;
            case SortOption.ConfirmedShipDateDescending:
                sorting = {
                    columnName: 'CONFIRMEDSHIPDATE',
                    isDescending: true
                };
                break;
            default:
                break;
        }

        return sorting;
    };

    /**
     * Gets the header.
     * @returns Header props.
     */
    private readonly _getHeaderProps = (): IOrderHistoryWithSearchAndFiltersHeaderProps => {
        const {
            context,
            id,
            typeName,
            config: { heading }
        } = this.props;

        return {
            context: context,
            id: id,
            typeName: typeName,
            data: {
                salesOrders: this.orderHistoryWithSearchAndFilters.salesOrders
            },
            className: `${this.orderHistoryClassName}__header`,
            resources: {
                orderCountLabel: this.props.resources.orderCountLabel,
                ordersCountLabel: this.props.resources.ordersCountLabel
            },
            textProps: heading,
            showOrderCount: false,
            onChange: this._handleOrderHistoryHeadingChange
        } as IOrderHistoryWithSearchAndFiltersHeaderProps;
    };

    /**
     * Gets the orders view mode selector.
     * @returns Orders view mode selector props.
     */
    private readonly _getOrdersViewModeProps = (): IOrderHistoryWithSearchAndFiltersViewModeProps | undefined => {
        const { context, id, typeName } = this.props;

        if (this.props.config.isGridViewEnabled) {
            return {
                context: context,
                id: id,
                typeName: typeName,
                data: {},
                className: `${this.orderHistoryClassName}__orders-view-mode-selector`,
                resources: {
                    listViewOptionAriaLabel: this.props.resources.orderHistoryListViewModeAriaLabel,
                    detailedViewOptionAriaLabel: this.props.resources.orderHistoryDetailedViewModeAriaLabel
                },
                currentViewModeState: this._ordersViewModeState,
                telemetryContent: this.telemetryContent,
                onClick: this._clickOrderViewMode
            } as IOrderHistoryWithSearchAndFiltersViewModeProps;
        }

        return;
    };

    /**
     * Gets organization wide order history selector.
     * @returns Organization wide order history selector props.
     */
    private readonly _getOrganizationWideSelectorProps = (): IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps | undefined => {
        const { context, id, typeName } = this.props;

        if (this.props.data.customerInformation && this.props.data.customerInformation.result) {
            return {
                context: context,
                id: id,
                typeName: typeName,
                className: `${this.orderHistoryClassName}__organization-wide-selector`,
                resources: {
                    currentUserOptionLabel: this.props.resources.orderHistoryFilterYourOrderHistory,
                    organizationWideOptionLabel: this.props.resources.orderHistoryFilterOrganizationWide,
                    showAllOptionLabel: this.props.resources.orderHistoryFilterShowAll
                },
                currentSelectorState: this._organizationWideSelectorState,
                isBusinessPartnerUserAdmin: this._isB2bAdmin,
                isRetailB2BContactMultiOutletOrderingFeatureEnabled: this._isB2bContact,
                data: { customer: this.props.data.customerInformation.result },
                onChange: this._clickOrganizationWideSelectorOption
            } as IOrderHistoryWithSearchAndFiltersOrganizationWideSelectorProps;
        }

        return;
    };

    /**
     * Gets the search bar.
     * @returns Search bar props.
     */
    private readonly _getSearchBarProps = (): ISearchBarProps => {
        const { context, id: moduleId, typeName } = this.props;

        const options = (this.props.data.searchOptions?.result ?? []).map(option =>
            mapSearchOptionIntoISearchBarOption(option, this.props.resources)
        );

        // set the default search option as the first option of list if any; otherwise, set it to undefined
        this._searchBarState.currentSearchOption = this._searchBarState.currentSearchOption ?? options[0];

        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            className: `${this.orderHistoryClassName}__search-bar`,
            currentSearchOption: this._searchBarState.currentSearchOption,
            currentSearchText: this._searchBarState.currentSearchText,
            resources: {
                searchOptionsAriaLabel: this.props.resources.searchOptionsAriaLabel,
                searchBarPlaceholder: this.props.resources.searchBarPlaceholder,
                searchButtonAriaLabel: this.props.resources.searchButtonAriaLabel
            },
            data: {
                options: options
            },
            clearSearchTextOnOptionChange: true,
            onClick: this._clickSearchButton
        };
    };

    private readonly _loadOrganizations = async (): Promise<void> => {
        const {
            context: { actionContext, telemetry },
            resources: { organizationSelectionErrorMessage }
        } = this.props;

        await BusinessPartnersDataActions.getManagedBusinessPartnersAsync({ callerContext: actionContext })
            .then((organizationList: BusinessPartner[]) => {
                transaction(() => {
                    this._contactFilterState.organizationList = organizationList;
                    this._contactFilterState.businessPartnerErrorMessage = '';
                    this._contactFilterState.loaded = true;
                });
            })
            .catch(error => {
                telemetry.trace(error);
                transaction(() => {
                    this._contactFilterState.businessPartnerErrorMessage = organizationSelectionErrorMessage;
                    this._contactFilterState.loaded = true;
                });
            });
    };

    /**
     * Gets the unique contact in accordance with PartyRecId.
     * @returns Unique contact.
     */
    private _getUniqueContact = async (): Promise<void> => {
        const {
            context: { actionContext, telemetry }
        } = this.props;

        await BusinessPartnerUsersDataActions.getAccessibleUsersAsync({ callerContext: actionContext })
            .then(users => {
                transaction(() => {
                    this._contactFilterState.contact = users;
                });
            })
            .catch(error => {
                telemetry.trace(error);
                transaction(() => {
                    this._contactFilterState.contact = [];
                });
            });
    };

    /**
     * Gets refiner options.
     * @returns Refiner options.
     */
    private readonly _getRefinerOptions = (): React.ReactNode[] => {
        const { context, id: moduleId, typeName } = this.props;

        const selectedRefinerItems = this._activeRefinementCriteria ?? [];
        const channelIdentities = (this.props.data.channels.result && this.props.data.channels.result.channelIdentities) ?? [];
        const dateRefinerOptions = this.props.data.dateRefinerOptions?.result ?? [];
        const orderStatusRefinerOptions = this.props.data.orderStatusRefinerOptions?.result ?? [];
        const organizations = this._contactFilterState.organizationList ?? [];
        const uniqueContacts = this._contactFilterState.contact ?? [];
        const contactSource: BusinessPartnerUser[] = this._contactFilterState.contact ?? [];
        const isAdmin = this._isB2bAdmin;
        const isRetailB2BContactMultiOutletOrderingFeatureEnabled = this._isB2bContact;

        const organizationView = this._organizationWideSelectorState;

        const getShowContactDisplay = (): boolean => {
            if (!isRetailB2BContactMultiOutletOrderingFeatureEnabled) {
                return false;
            }

            switch (organizationView) {
                case OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.OrganizationWide:
                    return isAdmin;

                case OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.ShowAll:
                    return uniqueContacts.length > 1;

                default:
                    return false;
            }
        };

        const showAllFilterDisplay =
            isRetailB2BContactMultiOutletOrderingFeatureEnabled &&
            organizationView === OrderHistoryWithSearchAndFiltersOrganizationWideSelectorState.ShowAll &&
            organizations.length > 1;
        const showContactDisplay = getShowContactDisplay();

        return [
            <RefinerOptionsComponent
                context={context}
                id={moduleId}
                typeName={typeName}
                key={RefinerSourceType.Origin}
                className={`${this.orderHistoryClassName}__refiner-options-${RefinerSourceType.Origin}`}
                isDisabled={false}
                isExpandedOnInitialLoad={true}
                telemetryContent={this.telemetryContent}
                onUpdateRefiners={this._toggleRefinerItem}
                data={{
                    refinerOptionsId: RefinerSourceType.Origin,
                    sourceTypeValue: RefinerSourceType.Origin,
                    name: this.props.resources.channelRefinerOptionLabel,
                    typeValue: RefinerOptionsType.MultiSelect,
                    isExpanded: true,
                    selectedRefinerItems: selectedRefinerItems,
                    refinerItems: channelIdentities?.map(channelIdentity => mapChannelIdentityIntoIRefinerItemData(channelIdentity))
                }}
            />,
            showAllFilterDisplay && (
                <RefinerOptionsComponent
                    context={context}
                    id={moduleId}
                    typeName={typeName}
                    key={RefinerSourceType.Organization}
                    className={`${this.orderHistoryClassName}__refiner-options-${RefinerSourceType.Organization}`}
                    isDisabled={false}
                    isExpandedOnInitialLoad={true}
                    telemetryContent={this.telemetryContent}
                    onUpdateRefiners={this._toggleRefinerItem}
                    data={{
                        refinerOptionsId: RefinerSourceType.Organization,
                        sourceTypeValue: RefinerSourceType.Organization,
                        name: this.props.resources.organizationRefinerOptionLabel,
                        typeValue: RefinerOptionsType.MultiSelect,
                        isExpanded: true,
                        selectedRefinerItems: selectedRefinerItems,
                        refinerItems: (this._contactFilterState.organizationList ?? []).map(mapOrganizationIntoIRefinerItemData)
                    }}
                />
            ),
            showContactDisplay && (
                <RefinerOptionsComponent
                    context={context}
                    id={moduleId}
                    typeName={typeName}
                    key={RefinerSourceType.Contact}
                    className={`${this.orderHistoryClassName}__refiner-options-${RefinerSourceType.Contact}`}
                    isDisabled={false}
                    isExpandedOnInitialLoad={true}
                    telemetryContent={this.telemetryContent}
                    onUpdateRefiners={this._toggleRefinerItem}
                    data={{
                        refinerOptionsId: RefinerSourceType.Contact,
                        sourceTypeValue: RefinerSourceType.Contact,
                        name: this.props.resources.contactRefinerOptionLabel,
                        typeValue: RefinerOptionsType.MultiSelect,
                        isExpanded: true,
                        selectedRefinerItems: selectedRefinerItems,
                        refinerItems: mapContactsIntoDeduplicatedRefinerItems(contactSource)
                    }}
                />
            ),
            <RefinerOptionsComponent
                context={context}
                id={moduleId}
                typeName={typeName}
                key={RefinerSourceType.CreatedDate}
                className={`${this.orderHistoryClassName}__refiner-options-${RefinerSourceType.CreatedDate}`}
                isDisabled={false}
                isExpandedOnInitialLoad={true}
                telemetryContent={this.telemetryContent}
                onUpdateRefiners={this._toggleRefinerItem}
                data={{
                    refinerOptionsId: RefinerSourceType.CreatedDate,
                    sourceTypeValue: RefinerSourceType.CreatedDate,
                    name: this.props.resources.createdDateRefinerOptionLabel,
                    typeValue: RefinerOptionsType.SingleSelect,
                    isExpanded: true,
                    selectedRefinerItems: selectedRefinerItems,
                    refinerItems: dateRefinerOptions?.map(dateRefinerOptionItem =>
                        mapDateRefinerOptionIntoIRefinerItemData(dateRefinerOptionItem, this.props.resources)
                    )
                }}
            />,
            <RefinerOptionsComponent
                context={context}
                id={moduleId}
                typeName={typeName}
                key={RefinerSourceType.OrderStatus}
                className={`${this.orderHistoryClassName}__refiner-options-${RefinerSourceType.OrderStatus}`}
                isDisabled={false}
                isExpandedOnInitialLoad={true}
                telemetryContent={this.telemetryContent}
                onUpdateRefiners={this._toggleRefinerItem}
                data={{
                    refinerOptionsId: RefinerSourceType.OrderStatus,
                    sourceTypeValue: RefinerSourceType.OrderStatus,
                    name: this.props.resources.orderStatusRefinerOptionLabel,
                    typeValue: RefinerOptionsType.SingleSelect,
                    isExpanded: true,
                    selectedRefinerItems: selectedRefinerItems,
                    refinerItems: orderStatusRefinerOptions?.map(orderStatusRefinerOptionItem =>
                        mapOrderStatusRefinerOptionIntoIRefinerItemData(orderStatusRefinerOptionItem, this.props.resources)
                    )
                }}
            />
        ];
    };

    /**
     * Gets the refiner.
     * @returns Refiner props.
     */
    private readonly _getRefinerProps = (): IRefinerProps => {
        const { context, id: moduleId, typeName } = this.props;
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            refinerOptions: this._getRefinerOptions(),
            isMobileView: this.isMobile,
            isExpanded: this._refinerState.isExpanded,
            refinerModalToggleProps: this._getRefinerModalToggleProps(),
            refinerModalProps: this._getRefinerModalProps(),
            refinerHeaderProps: this._getRefinerHeaderProps(),
            refinerFooterProps: this._getRefinerFooterProps()
        };
    };

    /**
     * Gets the refiner modal toggle.
     * @returns Refiner modal toggle props.
     */
    private readonly _getRefinerModalToggleProps = (): IRefinerModalToggleProps => {
        const { resources, context, id: moduleId, typeName } = this.props;

        return {
            context: context,
            id: `${moduleId}__refiner-modal-toggle`,
            typeName: typeName,
            data: {},
            text: resources.filterAndSortText,
            ariaLabel: resources.filterAndSortTextAriaLabel,
            innerRef: this._refinerModalToggleRef,
            onClick: this._toggleRefinerModal
        };
    };

    /**
     * Gets the refiner modal.
     * @returns Refiner modal props.
     */
    private readonly _getRefinerModalProps = (): IRefinerModalProps => {
        const { resources, context, id: moduleId, typeName } = this.props;

        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            modalTitle: resources.filterAndSortText,
            modalCloseButtonText: resources.doneText,
            isOpen: this._refinerState.isModalOpen,
            returnRef: this._refinerModalToggleRef,
            refinerSortProps: this._getRefinerSortProps(),
            refinerOptions: this._getRefinerOptions(),
            onToggle: () => {
                this._toggleRefinerModal();
                this._applyRefinerChoices();
            }
        };
    };

    /**
     * Gets the refiner header.
     * @returns Refiner header props.
     */
    private readonly _getRefinerHeaderProps = (): IRefinerHeaderProps => {
        const { context, id: moduleId, typeName } = this.props;
        const ordersViewModeComponentProps = this._getOrdersViewModeProps();
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            refinerToggleProps: this._getRefinerToggleProps(),
            refinerChoiceSummaryProps: this._getRefinerChoiceSummaryProps(),
            refinerSortProps: this.props.config?.showSortingOptions ? this._getRefinerSortProps() : undefined,
            extraActions: ordersViewModeComponentProps && (
                <OrderHistoryWithSearchAndFiltersViewModeComponent {...ordersViewModeComponentProps} />
            )
        };
    };

    /**
     * Gets the refiner toggle.
     * @returns Refiner toggle props.
     */
    private readonly _getRefinerToggleProps = (): IRefinerToggleProps => {
        const { context, id: moduleId, typeName, resources } = this.props;
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            text: resources.filterText,
            ariaLabel: resources.filterText,
            isExpanded: this._refinerState.isExpanded,
            onClick: () => {
                this._refinerState.isExpanded = !this._refinerState.isExpanded;
            }
        };
    };

    /**
     * Gets the refiner choice summary.
     * @returns Refiner choice summary props.
     */
    private readonly _getRefinerChoiceSummaryProps = (): IRefinerChoiceSummaryProps => {
        const { context, id: moduleId, typeName, resources } = this.props;
        const selectedRefiners = this._activeRefinementCriteria ?? [];
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: { selectedChoices: selectedRefiners },
            clearAllText: resources.clearAllText,
            label: resources.choiceSummaryLabel,
            choiceAriaLabel: resources.choiceAriaLabel,
            choiceFormat: resources.orderHistoryChoiceFormat,
            closeAriaLabel: resources.closeAriaLabel,
            telemetryContent: this.telemetryContent,
            onClickChoice: this._clickRefinerChoice,
            onClickClearAll: this._clickRefinerClearAll
        };
    };

    /**
     * Gets the refiner sort.
     * @returns Refiner sort props.
     */
    private readonly _getRefinerSortProps = (): IRefinerSortProps => {
        const { context, id: moduleId, typeName, resources } = this.props;

        // fetch the sorting options
        const options = (this.props.data.sortOptions?.result ?? []).map(option =>
            mapSortOptionIntoIRefinerSortOption(option, this.props.resources)
        );

        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: { options: options },
            className: `${this.orderHistoryClassName}__refiner-sort`,
            currentSortOption: this._refinerState.currentSortOption,
            label: resources.sortLabel,
            ariaLabel: resources.sortAriaLabel,
            telemetryContent: this.telemetryContent,
            onSelectOption: this._selectRefinerSortOption
        };
    };

    /**
     * Gets the refiner footer.
     * @returns Refiner footer props.
     */
    private readonly _getRefinerFooterProps = (): IRefinerFooterProps => {
        const { context, id: moduleId, typeName } = this.props;
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            refinerApplyProps: this._getRefinerApplyProps()
        };
    };

    /**
     * Gets the refiner apply.
     * @returns Refiner apply props.
     */
    private readonly _getRefinerApplyProps = (): IRefinerApplyProps => {
        const { context, id: moduleId, typeName, resources } = this.props;
        return {
            context: context,
            id: moduleId,
            typeName: typeName,
            data: {},
            text: resources.doneText,
            ariaLabel: resources.doneTextAriaLabel,
            telemetryContent: this.telemetryContent,
            onClick: () => {
                this._refinerState.isExpanded = false;
                this._applyRefinerChoices();
            }
        };
    };
}

export default OrderHistoryWithSearchAndFilters;
